/**
 * 佛山口腔报告（健康报告）分享/预览参数生成工具
 * 与小程序端 shareVerification 落地页配套：
 *   target: healthRecords - 健康档案（默认，不传target时的历史行为）
 *   target: healthReport  - 佛山口腔报告/健康报告，需额外携带 report_id
 */
import crypto from "@/util/crypto.js";

// 验证类型：staff-员工预览 patient-推送给患者本人 share-好友分享验证后进入
export const REPORT_VERIFY_TYPE = {
  STAFF: "staff",
  PATIENT: "patient",
  SHARE: "share",
};

// 验证成功后跳转目标页面
export const REPORT_TARGET = {
  HEALTH_RECORDS: "healthRecords",
  HEALTH_REPORT: "healthReport",
};

// 小程序分享验证落地页路径
const SHARE_VERIFICATION_PAGE =
  "pages/healthRecords/shareVerification/shareVerification.html";

// 分享消息默认有效期：48小时
const DEFAULT_SHARE_EXPIRE_HOURS = 48;

// 分享消息默认封面图
const DEFAULT_SHARE_IMAGE =
  "https://ygj-cdn-static.oss-cn-beijing.aliyuncs.com/scrm/images/home/da.png";

/**
 * 对分享验证明文参数进行AES加密，拼接成小程序落地页跳转路径
 * 不传target时走历史默认行为（healthRecords-健康档案），无需report_id
 * @param {Object} params - 分享验证明文参数
 * @returns {string} 小程序页面路径（含加密data参数）
 */
export function buildShareVerificationPath(params) {
  const encryptedParams = crypto.encrypt(JSON.stringify(params));
  return `${SHARE_VERIFICATION_PAGE}?data=${encodeURIComponent(
    encryptedParams
  )}`;
}

/**
 * 生成员工预览佛山口腔报告（健康报告）的小程序跳转路径
 * 不做身份/手机号校验，员工可直接进入指定报告
 * @param {string} patientId - 患者ID
 * @param {string} reportId - 报告ID
 * @returns {string} 小程序页面路径
 */
export function generateHealthReportPreviewPath(patientId, reportId) {
  const previewParams = {
    type: REPORT_VERIFY_TYPE.STAFF,
    patient_id: patientId,
    target: REPORT_TARGET.HEALTH_REPORT,
    report_id: reportId,
  };
  return buildShareVerificationPath(previewParams);
}

/**
 * 生成推送佛山口腔报告（健康报告）给患者本人的分享消息配置
 * 患者点击后需完成手机号绑定/核对校验才能查看指定报告
 * @param {Object} options
 * @param {string} options.patientId - 患者ID
 * @param {string} options.reportId - 报告ID
 * @param {string} options.clinicId - 诊所ID
 * @param {string} options.tenantId - 租户ID
 * @param {string} [options.patientName] - 患者姓名，用于拼接消息标题，缺省使用通用标题
 * @param {number} [options.expireHours] - 分享链接有效期（小时），默认48小时
 * @returns {{msgTitle: string, imgUrl: string, pageUrl: string}} 分享消息配置
 */
export function generateHealthReportShareInfo({
  patientId,
  reportId,
  clinicId,
  tenantId,
  patientName = "",
  expireHours = DEFAULT_SHARE_EXPIRE_HOURS,
}) {
  const expireTime = Date.now() + expireHours * 60 * 60 * 1000;

  const shareParams = {
    type: REPORT_VERIFY_TYPE.PATIENT,
    patient_id: patientId,
    clinic_id: clinicId,
    tenant_id: tenantId,
    expire_time: expireTime,
    target: REPORT_TARGET.HEALTH_REPORT,
    report_id: reportId,
  };

  const pageUrl = buildShareVerificationPath(shareParams);
  const msgTitle = patientName
    ? `${patientName}您好，点击查收您的健康报告`
    : "您好，点击查收您的健康报告";

  return {
    msgTitle,
    imgUrl: DEFAULT_SHARE_IMAGE,
    pageUrl,
  };
}
